import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from './Header';
import db from '../services/db';
import { useAuth } from '../hooks/useAuth';

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [siteText, setSiteText] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await db.getTable('siteText');
        setSiteText(data);
      } catch (e) {
        console.error("Settings fetch failed", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []); // Run only once on mount, keep settings across navigation

  const getSetting = (key) => siteText.find(s => s.key === key)?.content;

  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  const isStudentPage = currentPath.startsWith('/student');
  const isTeacherPage = currentPath.startsWith('/teacher');
  const isGamePage = currentPath.startsWith('/play-games');
  const isOnlineLessonPage = currentPath.startsWith('/online-lesson');
  const isSelfStudyLessonPage = currentPath.startsWith('/self-study-lesson');
  const isLoginPage = currentPath === '/login';
  const isOpenExercisePage = currentPath.startsWith('/open');
  const isPortalPage = currentPath.startsWith('/portal');
  const isPortalCheckerPage = currentPath.startsWith('/portal/checker') || currentPath.startsWith('/portal/checks');
  const isCheckerLandingPage = currentPath === '/checker' || currentPath === '/checker_teachers';
  const isLegalPage = currentPath === '/legal';
  const isMobileTeacherHome = isTeacherPage && new URLSearchParams(location.search).get('tab') === 'MOBILE_HOME';

  // Visibility logic
  const isMasterHidden = getSetting('is_site_hidden') === 'true';
  const showHome = getSetting('show_home') !== 'false';
  const showCourses = getSetting('show_courses') !== 'false';
  const showPrices = getSetting('show_prices') !== 'false';

  const role = user?.role || user?.user_metadata?.role;
  const isTeacher = role === 'teacher';
  const isStudent = role === 'student';
  const isPortal = role === 'portal';

  let blockReason = null;

  // Guard logic:
  // 1. Logged in users (Students/Teachers) are NEVER blocked from their respective dashboards/games
  // 2. Only check visibility for the root, courses, and prices pages
  // 3. Logged out users or guests on non-dashboard pages are subject to visibility blocks
  
  const isDashboardPath = isStudentPage || isTeacherPage || isGamePage || isOnlineLessonPage || isSelfStudyLessonPage || isLoginPage || isPortalPage || isCheckerLandingPage;

  if (!isTeacher && !isDashboardPath) {
    if (isMasterHidden) {
      blockReason = "Site Maintenance";
    } else if (currentPath === '/' && !showHome) {
      blockReason = "Not Available";
    } else if (currentPath.startsWith('/courses') && !showCourses) {
      blockReason = "Not Available";
    } else if (currentPath.startsWith('/prices') && !showPrices) {
      blockReason = "Not Available";
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#64748b', fontWeight: 600 }}>
        Loading…
      </div>
    );
  }

  if (blockReason) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-secondary px-4 text-center">
        <div className="card max-w-lg p-12 shadow-xl animate-fade-in" style={{ borderRadius: '32px', background: 'white', position: 'relative' }}>
          <div className="text-6xl mb-6">{blockReason === "Site Maintenance" ? '🚧' : '🔒'}</div>
          <h1 className="font-header text-4xl mb-4">{blockReason}</h1>
          <p className="text-secondary text-lg mb-8">
            {blockReason === "Site Maintenance"
              ? "Our site is undergoing some updates. Please check back later."
              : "This section is currently unavailable. Please explore other parts of our site."}
          </p>
          <div className="flex flex-col gap-4">
            {user ? (
               <>
                 <Link to={isTeacher ? '/teacher' : isPortal ? '/portal' : '/student'} className="btn btn-primary py-3">Go to my Dashboard</Link>
                 <button onClick={async () => { await logout(); window.location.href = '/'; }} className="text-sm font-bold text-slate-400 hover:text-slate-600 underline">Logout from {user.email || 'account'}</button>
               </>
            ) : (
               <>
                 <Link to="/" className="btn btn-primary py-3">Back to Home</Link>
                 <Link to="/login" className="btn btn-secondary py-3" style={{ border: '1px solid #e2e8f0' }}>Student Login</Link>
               </>
            )}
          </div>
        </div>
        <style dangerouslySetInnerHTML={{
          __html: `
            .min-vh-100 { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f8fafc; }
        ` }} />
      </div>
    );
  }

  return (
    <div className={`layout-root${isMobileTeacherHome ? ' layout-root--mobile-teacher-home' : ''}`}>
      {!isStudentPage && !isGamePage && !isOnlineLessonPage && !isSelfStudyLessonPage && !isPortalPage && !isCheckerLandingPage && !isLoginPage && !isMobileTeacherHome && (
        <Header siteText={siteText} minimal={isOpenExercisePage} />
      )}
      <main className="main-content">
        {children}
      </main>
      {!isStudentPage && !isTeacherPage && !isGamePage && !isOnlineLessonPage && !isSelfStudyLessonPage && !isPortalPage && !isCheckerLandingPage && !isLoginPage && (
        <footer className="site-footer">
          <div className="container site-footer-inner">
            <div className="site-footer-left">
              <span className="site-footer-muted">© {new Date().getFullYear()} Пятовский А. Ю.</span>
            </div>
            <div className="site-footer-right">
              <Link to="/legal" className="site-footer-link" aria-current={isLegalPage ? 'page' : undefined}>
                Правовая информация
              </Link>
            </div>
          </div>
        </footer>
      )}
      <style dangerouslySetInnerHTML={{ __html: `
        .layout-root {
          min-height: 100vh;
          background-color: #ffffff;
        }
        /* Document scroll — nested overflow:auto on Mobile Home broke wheel/touch
           (scrollbar drag still worked). Keep min-height only; overlays lock body. */
        .layout-root--mobile-teacher-home {
          min-height: 100dvh;
          overflow: visible;
        }
        .layout-root--mobile-teacher-home .main-content {
          min-height: 100dvh;
          overflow: visible;
        }
        .site-footer {
          border-top: 1px solid rgba(226, 232, 240, 0.7);
          padding: 18px 0;
          background: rgba(248, 250, 252, 0.6);
        }
        .site-footer-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }
        .site-footer-muted { color: #64748b; font-size: 13px; font-weight: 600; }
        .site-footer-link { color: var(--text-secondary, #334155); font-size: 13px; font-weight: 700; text-decoration: underline; }
        .site-footer-link:hover { color: var(--primary, #4361ee); }
      ` }} />
    </div>
  );
};

export default Layout;
